import { useState } from "react";
import { router } from "@inertiajs/react";
import Header from "../Component/Header";

export default function Detail({ data }) {
    const resolveMemberPhoto = (photo) => {
        if (!photo) return null;
        if (/^https?:\/\//i.test(photo)) return photo;
        return `/storage/${photo}`;
    };
    const memberStatus = Number(data?.mem_status ?? 0);

    const [form, setForm] = useState({
        name: data?.user?.name ?? "",
        email: data?.user?.email ?? "",
        mem_code: data?.mem_code ?? "",
        mem_phone: data?.mem_phone ?? "",
        momo_code: data?.momo_code ?? "",
        mem_bank_name: data?.mem_bank_name ?? "",
        mem_bank_account_name: data?.mem_bank_account_name ?? "",
        mem_bank_account_number: data?.mem_bank_account_number ?? "",
        mem_status: memberStatus,
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);
    const [notification, setNotification] = useState(null);
    const [temporaryPassword, setTemporaryPassword] = useState("");
    const [photoPreview, setPhotoPreview] = useState(
        resolveMemberPhoto(data?.mem_photo)
    );

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const statusBadgeClass =
        memberStatus === 1
            ? "bg-green-100 text-green-700"
            : memberStatus === 2
              ? "bg-red-100 text-red-700"
              : "bg-amber-100 text-amber-700";

    const submit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setErrors({});
        setNotification(null);

        const formData = new FormData();
        formData.append("_method", "PUT");
        formData.append("name", form.name);
        formData.append("email", form.email);
        formData.append("mem_code", form.mem_code);
        formData.append("mem_phone", form.mem_phone);
        formData.append("momo_code", form.momo_code);
        formData.append("mem_status", String(form.mem_status));
        try {
            await window.axios.post(`/panel/members/${data?.mem_id}`, formData, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "multipart/form-data",
                },
            });

            const message = "Member changes saved successfully.";
            setNotification({
                type: "success",
                message,
            });
            window.alert(message);
            router.reload({ only: ["data"] });
        } catch (error) {
            setErrors(error?.response?.data?.errors ?? {});
            setNotification({
                type: "error",
                message:
                    error?.response?.data?.message ??
                    "Failed to save member changes. Please check the form and try again.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const resetPassword = async () => {
        const confirmed = window.confirm(
            `Generate a new temporary password for ${data?.user?.name ?? "this member"}?`
        );

        if (!confirmed) {
            return;
        }

        setResettingPassword(true);
        setNotification(null);
        setTemporaryPassword("");

        try {
            const response = await window.axios.post(
                `/panel/members/${data?.mem_id}/reset-password`
            );

            const temporaryPassword =
                response?.data?.data?.temporary_password ?? "";

            setNotification({
                type: "success",
                message: response?.data?.message ?? "Temporary password generated successfully.",
            });
            setTemporaryPassword(temporaryPassword);
        } catch (error) {
            setNotification({
                type: "error",
                message:
                    error?.response?.data?.message ??
                    "Failed to reset member password.",
            });
        } finally {
            setResettingPassword(false);
        }
    };

    const copyTemporaryPassword = async () => {
        if (!temporaryPassword) {
            return;
        }

        try {
            await navigator.clipboard.writeText(temporaryPassword);
            setNotification({
                type: "success",
                message: "Temporary password copied to clipboard.",
            });
        } catch (error) {
            window.prompt("Copy temporary password:", temporaryPassword);
        }
    };

    return (
        <>
            <Header />
            <main id="content" className="pb-14 sm:pb-16">
                <div className="max-w-[85rem] px-4 sm:px-6 lg:px-8 mx-auto">
                    <div className="py-2 sm:pb-0 sm:pt-5 space-y-5">
                        <div className="flex flex-wrap justify-between items-center gap-3">
                            <div>
                                <p className="text-sm text-muted-foreground-1">Member</p>
                                <h1 className="text-lg font-semibold text-foreground">Member Detail</h1>
                            </div>
                            <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${statusBadgeClass}`}>
                                {data?.mem_status_label ?? "Pending"}
                            </span>
                            <button
                                type="button"
                                onClick={() => router.visit("/panel/members")}
                                className="py-2 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-layer border border-layer-line text-layer-foreground hover:bg-layer-hover"
                            >
                                Back to Members
                            </button>
                        </div>

                        {notification && (
                            <div
                                className={`rounded-lg border px-4 py-3 text-sm ${
                                    notification.type === "success"
                                        ? "border-green-200 bg-green-50 text-green-700"
                                        : "border-red-200 bg-red-50 text-red-700"
                                }`}
                            >
                                {notification.message}
                            </div>
                        )}

                        {temporaryPassword && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <div className="font-medium">Temporary password</div>
                                        <div className="mt-1 break-all font-mono">{temporaryPassword}</div>
                                        <div className="mt-1 text-xs">
                                            Share it securely. The member must change it on next login.
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={copyTemporaryPassword}
                                        className="inline-flex items-center justify-center rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100"
                                    >
                                        Copy Password
                                    </button>
                                </div>
                            </div>
                        )}

                        <form onSubmit={submit} className="bg-card border border-card-line rounded-xl shadow-2xs p-5 space-y-6">
                            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                                <div className="flex items-start gap-4">
                                    {photoPreview ? (
                                        <img
                                            src={photoPreview}
                                            alt="Member preview"
                                            className="h-28 w-28 rounded-xl border border-layer-line object-cover"
                                        />
                                    ) : (
                                        <div className="flex h-28 w-28 items-center justify-center rounded-xl border border-dashed border-layer-line text-sm text-muted-foreground-1">
                                            No Photo
                                        </div>
                                    )}
                                    <div className="space-y-2">
                                        <h2 className="text-base font-semibold text-foreground">User Overview</h2>
                                        <p className="text-sm text-muted-foreground-1">
                                            Review member identity, payout account data, and approval status from one form.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${statusBadgeClass}`}>
                                                {data?.mem_status_label ?? "Pending"}
                                            </span>
                                            <span className="inline-flex items-center rounded-full border border-layer-line px-3 py-1 text-xs font-medium text-layer-foreground">
                                                User ID: {data?.user_id ?? "-"}
                                            </span>
                                            <span className="inline-flex items-center rounded-full border border-layer-line px-3 py-1 text-xs font-medium text-layer-foreground">
                                                Member ID: {data?.mem_id ?? "-"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 sm:flex-row">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="py-2 px-4 inline-flex items-center justify-center gap-x-2 text-sm font-medium rounded-lg bg-primary border border-primary-line text-primary-foreground hover:bg-primary-hover disabled:opacity-50 disabled:pointer-events-none"
                                    >
                                        {submitting ? "Saving..." : "Save Changes"}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={resettingPassword}
                                        onClick={resetPassword}
                                        className="py-2 px-4 inline-flex items-center justify-center gap-x-2 text-sm font-medium rounded-lg bg-layer border border-layer-line text-layer-foreground hover:bg-layer-hover disabled:opacity-50 disabled:pointer-events-none"
                                    >
                                        {resettingPassword ? "Generating..." : "Reset Password"}
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                                <section className="space-y-4 rounded-xl border border-card-line p-4">
                                    <h3 className="text-sm font-semibold text-foreground">Admin Controls</h3>

                                    <div>
                                        <label htmlFor="member-code" className="block mb-2 text-sm font-medium text-foreground">
                                            Member Code
                                        </label>
                                        <input
                                            id="member-code"
                                            type="text"
                                            value={form.mem_code}
                                            onChange={(e) => setField("mem_code", e.target.value)}
                                            className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg sm:text-sm text-layer-foreground"
                                        />
                                        {errors.mem_code && <p className="mt-1 text-xs text-red-600">{errors.mem_code}</p>}
                                    </div>

                                    <div>
                                        <label htmlFor="member-status" className="block mb-2 text-sm font-medium text-foreground">
                                            Approval Status
                                        </label>
                                        <select
                                            id="member-status"
                                            value={form.mem_status}
                                            onChange={(e) => setField("mem_status", Number(e.target.value))}
                                            className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg sm:text-sm text-layer-foreground"
                                        >
                                            <option value={0}>Pending</option>
                                            <option value={1}>Approved</option>
                                            <option value={2}>Rejected</option>
                                        </select>
                                        {errors.mem_status && <p className="mt-1 text-xs text-red-600">{errors.mem_status}</p>}
                                    </div>
                                </section>

                                <section className="space-y-4 rounded-xl border border-card-line p-4">
                                    <h3 className="text-sm font-semibold text-foreground">Identity</h3>

                                    <div>
                                        <label htmlFor="member-name" className="block mb-2 text-sm font-medium text-foreground">
                                            Name
                                        </label>
                                        <input
                                            id="member-name"
                                            type="text"
                                            value={form.name}
                                            onChange={(e) => setField("name", e.target.value)}
                                            className="py-2 px-3 block w-full rounded-lg border border-layer-line bg-layer text-layer-foreground sm:text-sm"
                                        />
                                        {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                                    </div>

                                    <div>
                                        <label htmlFor="member-email" className="block mb-2 text-sm font-medium text-foreground">
                                            Email
                                        </label>
                                        <input
                                            id="member-email"
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setField("email", e.target.value)}
                                            className="py-2 px-3 block w-full rounded-lg border border-layer-line bg-layer text-layer-foreground sm:text-sm"
                                        />
                                        {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                                    </div>

                                    <div>
                                        <label htmlFor="member-phone" className="block mb-2 text-sm font-medium text-foreground">
                                            Phone
                                        </label>
                                        <input
                                            id="member-phone"
                                            type="text"
                                            value={form.mem_phone}
                                            onChange={(e) => setField("mem_phone", e.target.value)}
                                            className="py-2 px-3 block w-full rounded-lg border border-layer-line bg-layer text-layer-foreground sm:text-sm"
                                        />
                                        {errors.mem_phone && <p className="mt-1 text-xs text-red-600">{errors.mem_phone}</p>}
                                    </div>

                                    <div>
                                        <label htmlFor="member-momo-code" className="block mb-2 text-sm font-medium text-foreground">
                                            Momo Code
                                        </label>
                                        <input
                                            id="member-momo-code"
                                            type="text"
                                            value={form.momo_code}
                                            onChange={(e) => setField("momo_code", e.target.value)}
                                            className="py-2 px-3 block w-full rounded-lg border border-layer-line bg-layer text-layer-foreground sm:text-sm"
                                        />
                                        {errors.momo_code && <p className="mt-1 text-xs text-red-600">{errors.momo_code}</p>}
                                    </div>
                                </section>

                                <section className="space-y-4 rounded-xl border border-card-line p-4 xl:col-span-2">
                                    <h3 className="text-sm font-semibold text-foreground">Payout Bank Information</h3>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                        <div>
                                            <label htmlFor="member-bank-name" className="block mb-2 text-sm font-medium text-foreground">
                                                Bank Name
                                            </label>
                                            <input
                                                id="member-bank-name"
                                                type="text"
                                                value={form.mem_bank_name}
                                                readOnly
                                                className="py-2 px-3 block w-full rounded-lg border border-layer-line bg-surface text-layer-foreground sm:text-sm opacity-80 cursor-not-allowed"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="member-bank-account-name" className="block mb-2 text-sm font-medium text-foreground">
                                                Account Name
                                            </label>
                                            <input
                                                id="member-bank-account-name"
                                                type="text"
                                                value={form.mem_bank_account_name}
                                                readOnly
                                                className="py-2 px-3 block w-full rounded-lg border border-layer-line bg-surface text-layer-foreground sm:text-sm opacity-80 cursor-not-allowed"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="member-bank-account-number" className="block mb-2 text-sm font-medium text-foreground">
                                                Account Number
                                            </label>
                                            <input
                                                id="member-bank-account-number"
                                                type="text"
                                                value={form.mem_bank_account_number}
                                                readOnly
                                                className="py-2 px-3 block w-full rounded-lg border border-layer-line bg-surface text-layer-foreground sm:text-sm opacity-80 cursor-not-allowed"
                                            />
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </form>
                    </div>
                </div>
            </main>
        </>
    );
}
